import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { type BaseRange, type Editor, Range as SlateRange } from 'slate';
import { ReactEditor, useSlateSelector } from 'slate-react';

import { type ElementOffset, getOffsetWithin } from '../lib/utils';

interface Params {
  editor: ReactEditor;
  rootElement: HTMLElement | null;
  readOnly: boolean;
}

export interface TextSelection {
  range: BaseRange;
  position: ElementOffset;
  // Where the selected text ends: the right end of its last line.
  end: Pick<ElementOffset, 'top' | 'left'>;
}

const measureSelection = (rootElement: HTMLElement, domRange: Range) => {
  const position = getOffsetWithin(rootElement, domRange.getBoundingClientRect());
  const lastLine = [...domRange.getClientRects()].filter((rect) => rect.width > 0).at(-1);
  const end = lastLine
    ? getOffsetWithin(rootElement, lastLine)
    : { top: position.top, right: position.right };
  return { position, end: { top: end.top, left: end.right } };
};

const getExpandedSelection = (editor: Editor) =>
  editor.selection && SlateRange.isExpanded(editor.selection) ? editor.selection : null;

const isSameSelection = (a: BaseRange | null, b: BaseRange | null) =>
  a === b || (a !== null && b !== null && SlateRange.equals(a, b));

/**
 * Slate tracks the selection only while the editor is editable. A read-only editor leaves it to the
 * DOM, so the DOM selection is mapped back to a Slate range here.
 *
 * An editable editor is followed through Slate's own selection. Slate writes `editor.selection` to
 * the DOM on every render, and it reads the DOM selection back with a delay. A render on a DOM
 * `selectionchange` would put an older selection back.
 */
export const useTextSelection = ({ editor, rootElement, readOnly }: Params) => {
  const [selection, setSelection] = useState<TextSelection | null>(null);
  const slateSelection = useSlateSelector(getExpandedSelection, isSameSelection);

  const showSlateSelection = useCallback(
    (range: BaseRange | null) => {
      if (!rootElement || !range) {
        setSelection(null);
        return;
      }
      try {
        const domRange = ReactEditor.toDOMRange(editor, range);
        setSelection({ range, ...measureSelection(rootElement, domRange) });
      } catch {
        setSelection(null);
      }
    },
    [editor, rootElement]
  );

  useLayoutEffect(() => {
    if (!readOnly) {
      showSlateSelection(slateSelection);
    }
  }, [readOnly, slateSelection, showSlateSelection]);

  // Selecting the same text again after the bar's action leaves Slate's selection as it was, so the
  // effect above does not run. The bar comes back once the mouse is released over that selection.
  useEffect(() => {
    if (readOnly || !rootElement) {
      return undefined;
    }

    const handleMouseUp = () => {
      const current = getExpandedSelection(editor);
      const domSelection = window.getSelection();
      const domRange =
        domSelection &&
        ReactEditor.toSlateRange(editor, domSelection, { exactMatch: false, suppressThrow: true });
      if (current && isSameSelection(current, domRange)) {
        showSlateSelection(current);
      }
    };

    const editorElement = ReactEditor.toDOMNode(editor, editor);
    editorElement.addEventListener('mouseup', handleMouseUp);
    return () => editorElement.removeEventListener('mouseup', handleMouseUp);
  }, [editor, rootElement, readOnly, showSlateSelection]);

  useEffect(() => {
    if (!rootElement || !readOnly) {
      return undefined;
    }

    const handleSelectionChange = () => {
      const domSelection = window.getSelection();
      if (!domSelection || domSelection.rangeCount === 0 || domSelection.isCollapsed) {
        setSelection(null);
        return;
      }

      const editorElement = ReactEditor.toDOMNode(editor, editor);
      if (
        !editorElement.contains(domSelection.anchorNode) ||
        !editorElement.contains(domSelection.focusNode)
      ) {
        return;
      }

      const range = ReactEditor.toSlateRange(editor, domSelection, {
        exactMatch: false,
        suppressThrow: true,
      });
      if (!range) {
        return;
      }

      setSelection({ range, ...measureSelection(rootElement, domSelection.getRangeAt(0)) });
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [editor, rootElement, readOnly]);

  // Only the bar is hidden. Slate writes its own selection to the DOM on every render, so changing it
  // here undoes the text a double-click is selecting and takes the focus from the composer.
  const clearSelection = useCallback(() => {
    if (readOnly) {
      window.getSelection()?.removeAllRanges();
    }
    setSelection(null);
  }, [readOnly]);

  return { selection, clearSelection };
};
